/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/dashboards/FnbKpiSection.tsx
 * PURPOSE: F&B KPI metric cards — Shift Completion, Avg Tasks/Shift, NPS
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-04-11
 * COMPLIANCE: SDLC 6.3.1 + DD-0039 API Contract + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: F&B Operations KPIs on Manager Dashboard
 * - Impact: MEDIUM — BKL Pilot (ISSUE-009 Step 6)
 * - Stakeholders: BKL GM (Huy), Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Component (separate file — CTO F1 merge owner policy)
 * - Pattern: TanStack Query + 3 metric cards
 * - Dependencies: React 18, apiV2, useQuery
 *
 * @cross-references
 * - Design: docs/02-design/18-adrs/06-decisions/DD-0039-fnb-kpi-manager-dashboard.md
 * - Sprint: SPRINT-095L S95L-013 (5 SP)
 * - Backend: GET /api/v2/tenant-admin/analytics/fnb-kpis
 * - Integration: ManagerDashboard.tsx imports this component (Local team)
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiV2 } from '../../services/api/config';

// ─── Types (DD-0039 contract) ───────────────────────────────────────────

interface FnbKpiData {
  shift_completion_rate: number;
  avg_tasks_per_shift: number;
  nps_score: number | null;
  nps_source: string | null;
  period: string;
  branch_id: string;
  as_of: string;
}

interface FnbKpiSectionProps {
  branchId: string | null;
}

// ─── Styles ─────────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  marginTop: '16px',
};

const headerStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: 'var(--nqh-text-primary, #111827)',
  marginBottom: '12px',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '12px',
};

const cardStyle: React.CSSProperties = {
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: 'var(--nqh-bg-surface, #ffffff)',
  border: '1px solid var(--nqh-border, #e5e7eb)',
  textAlign: 'center' as const,
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--nqh-text-secondary, #6b7280)',
  marginBottom: '4px',
};

const metricValueStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 700,
  color: 'var(--nqh-text-primary, #111827)',
};

const placeholderStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#9ca3af',
  fontStyle: 'italic',
};

const skeletonStyle: React.CSSProperties = {
  height: '80px',
  borderRadius: '12px',
  backgroundColor: '#f3f4f6',
  animation: 'pulse 1.5s ease-in-out infinite',
};

// ─── Component ──────────────────────────────────────────────────────────

export const FnbKpiSection: React.FC<FnbKpiSectionProps> = ({ branchId }) => {
  const { data, isLoading, error } = useQuery<FnbKpiData>({
    queryKey: ['manager', 'fnb-kpis', branchId],
    queryFn: async () => {
      if (!branchId) throw new Error('No branch');
      const response = await apiV2.get('/tenant-admin/analytics/fnb-kpis', {
        params: { branch_id: branchId },
      });
      return response.data;
    },
    enabled: !!branchId,
    refetchInterval: 5 * 60 * 1000, // DD-0039: cache 5 min
    staleTime: 4 * 60 * 1000,
    retry: 2,
  });

  if (!branchId) return null;

  if (isLoading) {
    return (
      <div style={sectionStyle}>
        <div style={headerStyle}>Chỉ số F&B</div>
        <div style={gridStyle}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={skeletonStyle} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={sectionStyle}>
        <div style={headerStyle}>Chỉ số F&B</div>
        <div style={{ ...cardStyle, color: '#ef4444', fontSize: '13px' }}>
          Không thể tải dữ liệu KPI
        </div>
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div style={headerStyle}>Chỉ số F&B</div>
      <div style={gridStyle}>
        {/* Card 1: Shift Completion Rate */}
        <div style={cardStyle}>
          <div style={metricLabelStyle}>Tỷ lệ hoàn thành ca</div>
          <div style={metricValueStyle}>
            {data ? `${Math.round(data.shift_completion_rate * 100)}%` : '—'}
          </div>
        </div>

        {/* Card 2: Avg Tasks/Shift */}
        <div style={cardStyle}>
          <div style={metricLabelStyle}>Năng suất trung bình</div>
          <div style={metricValueStyle}>
            {data ? data.avg_tasks_per_shift.toFixed(1) : '—'}
          </div>
        </div>

        {/* Card 3: NPS Score — DD-0039: null = "Chưa có dữ liệu" */}
        <div style={cardStyle}>
          <div style={metricLabelStyle}>Điểm NPS</div>
          {data?.nps_score !== null && data?.nps_score !== undefined ? (
            <div style={metricValueStyle}>{data.nps_score.toFixed(1)}</div>
          ) : (
            <div style={placeholderStyle}>Chưa có dữ liệu</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FnbKpiSection;
